import { useState } from "react";
import { toast } from "sonner";
import api, { humanError } from "@/lib/api";
import { ShieldCheck, Copy } from "lucide-react";

/**
 * Two-step TOTP enrolment.
 *
 * The secret is issued first and only switched on once the user has proved
 * their authenticator produces a matching code -- enabling on issue alone
 * would lock out anyone whose scan silently failed. Recovery codes are shown
 * exactly once, because the server keeps only their hashes.
 */
export default function TwoFactorSetup({ onEnabled }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);

  async function begin() {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function activate(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/activate", { code });
      setRecovery(data.recovery_codes);
      toast.success("Two-factor authentication is on");
    } catch (err) {
      setCode("");
      toast.error(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  if (recovery) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-[#E4E9EF] bg-white p-8 hs-card-shadow">
        <h2 className="font-display text-xl font-bold text-[#0B2B33]">Save your recovery codes</h2>
        <p className="mt-1 text-sm text-[#43535E]">
          Each code works once, if you lose your authenticator. This is the only time they are shown.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-[#F6F8FA] p-4 font-mono text-sm text-[#0B2B33]">
          {recovery.map((c) => <div key={c} data-testid="recovery-code">{c}</div>)}
        </div>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(recovery.join("\n"));
            toast.success("Copied");
          }}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1F5FD8] hover:underline"
        >
          <Copy className="h-4 w-4" /> Copy all
        </button>
        <button
          type="button"
          onClick={() => onEnabled?.()}
          data-testid="recovery-done-button"
          className="hs-btn-primary mt-6 w-full"
        >
          I have saved them
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-[#E4E9EF] bg-white p-8 hs-card-shadow">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-[#E8FBF1] text-[#16A46B]">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold text-[#0B2B33]">Set up two-factor authentication</h2>
          <p className="text-sm text-[#43535E]">Required before you can use the platform console.</p>
        </div>
      </div>

      {!setup ? (
        <>
          <p className="mt-5 text-sm text-[#43535E]">
            You will need an authenticator app such as 1Password, Authy, or Google Authenticator.
          </p>
          <button type="button" onClick={begin} disabled={busy} data-testid="2fa-begin-button"
            className="hs-btn-primary mt-6 w-full disabled:opacity-60">
            {busy ? "Starting…" : "Start setup"}
          </button>
        </>
      ) : (
        <form onSubmit={activate} className="mt-5 space-y-4">
          <div>
            <p className="text-sm text-[#43535E]">Add this key to your authenticator app:</p>
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#F6F8FA] p-3">
              <code className="flex-1 break-all font-mono text-sm text-[#0B2B33]" data-testid="2fa-secret">{setup.secret}</code>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(setup.secret); toast.success("Copied"); }}
                className="text-[#1F5FD8]" aria-label="Copy key">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="activate-code">
              Enter the 6-digit code
            </label>
            <input id="activate-code" type="text" required autoFocus value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="2fa-code-input" inputMode="numeric" autoComplete="one-time-code"
              className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-center text-lg tracking-[0.4em] text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
              placeholder="000000" />
          </div>
          <button type="submit" disabled={busy || code.length < 6} data-testid="2fa-activate-button"
            className="hs-btn-primary w-full disabled:opacity-60">
            {busy ? "Verifying…" : "Turn on two-factor"}
          </button>
        </form>
      )}
    </div>
  );
}
